import {useTranslation} from 'react-i18next';
import React, {Fragment, useEffect, useState} from 'react';
import Grid from '@mui/material/Grid';
import makeStyles from '@mui/styles/makeStyles';
import _ from 'lodash';
import moment from 'moment';
import {useSelector} from 'react-redux';
import {user} from '../../../../store/reducers/user';
import MessageBox from '../../../../util/messageBox';
import Notes from '../../../../util/notes';
import Formatter from '../../../../util/formatter';
import AgyleTextField from '../../../../components/ui/fields/agyleTextField';
import AgyleBlockSwitch from '../../../../components/ui/switch/agyleBlockSwitch';
import Typography from '@mui/material/Typography';
import AgyleDatePicker from '../../../../components/ui/fields/agyleDatePicker';
import AgyleButton from '../../../../components/ui/buttons/agyleButton';
import useOpenState from '../../../../hooks/useOpenState';
import MiniFormDialog from '../../../../components/modal/miniFormDialog';

const useStyles = makeStyles((theme) => ({
    background: {
        backgroundColor: theme.palette.background.paperDarker,
    },
    dialogTitle: {
        textAlign: 'center',
        padding: '10px',
    },
    dialogContent: {
        height: 'auto',
    },
    button: {
        background: '#fff',
        color: '#000',
        width: '10%',
        '&:hover': {
            background: '#fff',
        },
    },
    error: {
        color: '#f44336',
    },
    input: {
        width: '100%',
        fontSize: '15px',
        marginBottom: 10,
        borderRadius: '6%',
    },
    switchContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: 2,
    },
}));

const MedicationForm = (props) => {
    const {t} = useTranslation();
    const classesLocal = useStyles();
    const userObject = useSelector(user);

    const tester = Formatter.formatUserFullName(userObject);

    const [form, setForm] = useState({
        who: '',
        when: moment(),
        analgesia: false,
        analgesiaDescription: '',
        medication: false,
        medicationDescription: '',
    });

    const [isConfirmOpen, openConfirm, closeConfirm] = useOpenState(false, true);
    const [errorPicker, setErrorPicker] = useState(null);
    const [formIsModified, setFormIsModified] = React.useState(false);

    useEffect(() => {
        if (props.patient?.studies?.edau?.medicationForm) {
            const data = {
                ...props.patient?.studies?.edau?.medicationForm,
            };
            setForm(data);
        } else if (form.who === '') {
            setForm(prevState => ({
                ...prevState,
                who: tester,
            }));
            setFormIsModified(true);
        }
    }, []);

    const prepareNote = () => {
        return Notes.getMedicationFormNote(form);
    };

    const validation = () => {
        return (form.who && form.when && !errorPicker) || (!form.who && !form.when);
    };

    const submit = () => {
        if (validation() && !errorPicker) {
            let data = _.cloneDeep(form);
            const objToSave = ([
                {
                    name: 'studies.edau.medicationForm',
                    value: data,
                    noteToAdd: prepareNote(),
                },
            ]);

            props.onChange(objToSave);
            props.onClose();
        }
    };

    const clearData = () => {
        setForm({
            who: '',
            when: null,
            analgesia: false,
            analgesiaDescription: '',
            medication: false,
            medicationDescription: '',
        });
        closeConfirm();
    };

    return (
        <>
            <MiniFormDialog
                title={t(props._formTitle)}
                open={props.open}
                onClose={props.onClose}
                disableSubmit={!formIsModified}
                onSave={submit}
                formOptions={props._formOptions}
                buttons={[
                    <AgyleButton
                        color="tertiary"
                        onClick={openConfirm}
                        key="cancel"
                    >
                        {t('Cancel')}
                    </AgyleButton>,
                ]}
            >
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <AgyleTextField
                            label={t('Medical Practitioner')}
                            className={classesLocal.input}
                            onChange={event => {
                                setFormIsModified(true);
                                setForm(
                                    prevState => ({
                                        ...prevState,
                                        who: event.target.value,
                                    }),
                                );
                            }}
                            fullWidth
                            value={form.who}
                            noBorder={false}
                            withNewBackground
                            size="large"
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <AgyleDatePicker
                            label={t('Date and Time')}
                            value={form.when}
                            type="dateTime"
                            minDateTime={moment(props.patient?.admission?.startDate)}
                            maxDateTime={moment()}
                            onError={setErrorPicker}
                            onChange={event => {
                                setFormIsModified(true);
                                setForm(prevState => ({...prevState, when: moment(event).toISOString()}));
                            }}
                            noBorder={false}
                            withNewBackground
                            size="large"
                        />
                    </Grid>

                    <Grid item xs={12} sx={{pl: 1}} className={classesLocal.switchContainer}>
                        <AgyleBlockSwitch
                            onChange={event => setForm(prevState => {
                                    setFormIsModified(true);
                                    return ({
                                        ...prevState,
                                        analgesia: event,
                                        analgesiaDescription: event
                                            ? prevState.analgesiaDescription : '',
                                    });
                                },
                            )}
                            value={form.analgesia}
                        />

                        <Typography variant="body1" sx={{pl: 1}}>
                            {t('Analgesia Was Given')}
                        </Typography>
                    </Grid>

                    {form.analgesia &&
                        <Grid item xs={12}>
                            <AgyleTextField
                                label={t('Analgesia Description')}
                                className={classesLocal.input}
                                onChange={event => {
                                    setFormIsModified(true);
                                    setForm(
                                        prevState => ({
                                            ...prevState,
                                            analgesiaDescription: event.target.value,
                                        }),
                                    );
                                }}
                                multiline
                                fullWidth
                                rows={2}
                                value={form.analgesiaDescription}
                                noBorder={false}
                                withNewBackground
                                size="large"
                            />
                        </Grid>
                    }

                    <Grid item xs={12} sx={{pl: 1}} className={classesLocal.switchContainer}>
                        <AgyleBlockSwitch
                            onChange={event => setForm(prevState => {
                                    setFormIsModified(true);
                                    return ({
                                        ...prevState,
                                        medication: event,
                                        medicationDescription: event
                                            ? prevState.medicationDescription : '',
                                    });
                                },
                            )}
                            value={form.medication}
                        />

                        <Typography variant="body1" sx={{pl: 1}}>
                            {t('Regular Medication Been Prescribed')}
                        </Typography>
                    </Grid>

                    {form.medication &&
                        <Grid item xs={12}>
                            <AgyleTextField
                                label={t('Medication Description')}
                                className={classesLocal.input}
                                onChange={event => {
                                    setFormIsModified(true);
                                    setForm(
                                        prevState => ({
                                            ...prevState,
                                            medicationDescription: event.target.value,
                                        }),
                                    );
                                }}
                                fullWidth
                                multiline
                                rows={2}
                                value={form.medicationDescription}
                                noBorder={false}
                                withNewBackground
                                size="large"
                            />
                        </Grid>
                    }
                </Grid>
            </MiniFormDialog>

            <MessageBox
                onClose={closeConfirm}
                title={t('Confirmation')}
                message={t('Are you sure you want to clear entry?')}
                haveAccept={{yes: t('Submit'), no: t('Close')}}
                onAccept={clearData}
                open={isConfirmOpen}
            />
        </>
    );
};

export default MedicationForm;
